import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";
import {
  AlertTriangle,
  CheckCircle2,
  ChevronDown,
  Mail,
  RefreshCw,
  Send,
  XCircle,
} from "lucide-react";
import {
  getEmailVerification,
  getEmailVerificationAlerts,
  type VerificationAlert,
  type VerificationEvent,
} from "@/lib/email-log.functions";
import { sendAdminTestEmail } from "@/lib/admin-test-email.functions";
import { TemplateRecipientsCard } from "@/components/admin/TemplateRecipientsCard";
import { ResendFailedContactsCard } from "@/components/admin/ResendFailedContactsCard";
import { EmailQueueTimelineCard } from "@/components/admin/EmailQueueTimelineCard";
import { ContactNotificationDispatchCard } from "@/components/admin/ContactNotificationDispatchCard";
import { EmailVerificationAlertBanner } from "@/components/admin/EmailVerificationAlertBanner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Tabs,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const RANGE_OPTIONS = [
  { hours: 24, label: "24 t" },
  { hours: 24 * 7, label: "7 dage" },
  { hours: 24 * 30, label: "30 dage" },
] as const;

const ALERT_THRESHOLD_OPTIONS = [
  { hours: 1, label: "1 time" },
  { hours: 6, label: "6 timer" },
  { hours: 12, label: "12 timer" },
  { hours: 24, label: "24 timer" },
  { hours: 48, label: "48 timer" },
  { hours: 72, label: "72 timer" },
] as const;

const TEMPLATE_LABELS: Record<string, string> = {
  "admin-contact-notification": "Kontaktbesked",
  "admin-inquiry-notification": "Bilforespørgsel",
};

type StatusFilter = "all" | "sent" | "failed";

const STATUS_FILTER_OPTIONS: { value: StatusFilter; label: string }[] = [
  { value: "all", label: "Alle" },
  { value: "sent", label: "Kun sendt" },
  { value: "failed", label: "Kun fejl/DLQ" },
];

function eventMatchesFilters(
  event: VerificationEvent,
  filters: { status: StatusFilter; targetOnly: boolean; targetRecipient: string },
): boolean {
  if (filters.status === "sent" && event.status !== "sent") return false;
  if (filters.status === "failed" && !isFailureStatus(event.status)) return false;
  if (
    filters.targetOnly &&
    (event.recipient_email ?? "").toLowerCase() !== filters.targetRecipient
  ) {
    return false;
  }
  return true;
}

export const Route = createFileRoute("/admin/email-verifikation")({
  head: () => ({
    meta: [
      { title: "Email-verifikation – AutoXChange Admin" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: EmailVerificationPage,
});

function statusBadge(status: string) {
  if (status === "sent") {
    return (
      <Badge className="bg-emerald-500/15 text-emerald-700 hover:bg-emerald-500/20 border-emerald-500/30">
        Sendt
      </Badge>
    );
  }
  if (status === "pending") {
    return (
      <Badge className="bg-amber-500/15 text-amber-700 hover:bg-amber-500/20 border-amber-500/30">
        Afventer
      </Badge>
    );
  }
  if (status === "failed" || status === "dlq") {
    return (
      <Badge variant="destructive">{status === "dlq" ? "DLQ" : "Fejlet"}</Badge>
    );
  }
  if (status === "suppressed") {
    return <Badge variant="secondary">Undertrykt</Badge>;
  }
  return <Badge variant="outline">{status}</Badge>;
}

function formatDate(iso: string | null): string {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("da-DK", {
      dateStyle: "short",
      timeStyle: "short",
    });
  } catch {
    return iso;
  }
}

function EmailVerificationPage() {
  const fetchVerification = useServerFn(getEmailVerification);
  const fetchAlerts = useServerFn(getEmailVerificationAlerts);
  const sendTest = useServerFn(sendAdminTestEmail);
  const [hours, setHours] = useState<number>(24 * 7);
  const [alertThreshold, setAlertThreshold] = useState<number>(24);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [targetOnly, setTargetOnly] = useState<boolean>(false);
  const [lastTest, setLastTest] = useState<{
    messageId: string | null;
    status: string | null;
    createdAt: string | null;
    target: string;
    template: string;
    kind: "contact" | "inquiry";
    fieldChecks: { field: string; label: string; expected: string; found: boolean }[];
    allFieldsValid: boolean;
    renderError: string | null;
  } | null>(null);

  const query = useQuery({
    queryKey: ["admin-email-verification", hours],
    queryFn: () => fetchVerification({ data: { sinceHours: hours, perTemplateLimit: 25 } }),
    refetchInterval: 30_000,
  });

  const alertsQuery = useQuery({
    queryKey: ["admin-email-verification-alerts", alertThreshold],
    queryFn: () => fetchAlerts({ data: { thresholdHours: alertThreshold } }),
    refetchInterval: 60_000,
  });

  const testMutation = useMutation({
    mutationFn: (kind: "contact" | "inquiry" = "contact") =>
      sendTest({ data: { kind } }),
    onSuccess: (res) => {
      setLastTest({
        messageId: res.messageId,
        status: res.status,
        createdAt: res.createdAt,
        target: res.target,
        template: res.template,
        kind: res.kind,
        fieldChecks: res.fieldChecks ?? [],
        allFieldsValid: res.allFieldsValid ?? false,
        renderError: res.renderError ?? null,
      });
      const missing = (res.fieldChecks ?? []).filter((c) => !c.found);
      if (res.success && res.allFieldsValid) {
        toast.success(`Testmail sendt til ${res.target}`, {
          description: `Alle felter validerede · ${
            res.messageId ? `id ${res.messageId.slice(0, 8)}…` : "køet"
          }`,
        });
      } else if (res.success && !res.allFieldsValid) {
        toast.warning("Testmail sendt – men felter mangler", {
          description: `Mangler: ${missing.map((m) => m.label).join(", ") || "ukendt"}`,
        });
      } else {
        toast.error("Testmail kunne ikke afsendes", {
          description:
            (res.result as { error?: string; reason?: string })?.error ??
            (res.result as { reason?: string })?.reason ??
            "Ukendt fejl",
        });
      }
      // refresh the verification data so the new event shows up
      void query.refetch();
      void alertsQuery.refetch();
    },
    onError: (err: Error) => {
      toast.error("Testmail fejlede", { description: err.message });
    },
  });

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight flex items-center gap-2">
            <Mail className="h-6 w-6 text-primary" />
            Email-verifikation
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Bekræfter at notifikations-mails for kontaktbeskeder og bilforespørgsler bliver sendt
            – og når frem til <span className="font-mono">{query.data?.targetRecipient ?? "ss@autoxchange.dk"}</span>.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Tabs value={String(hours)} onValueChange={(v) => setHours(Number(v))}>
            <TabsList>
              {RANGE_OPTIONS.map((o) => (
                <TabsTrigger key={o.hours} value={String(o.hours)}>
                  {o.label}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
          <Button
            variant="outline"
            size="icon"
            onClick={() => query.refetch()}
            disabled={query.isFetching}
            aria-label="Opdater"
          >
            <RefreshCw className={`h-4 w-4 ${query.isFetching ? "animate-spin" : ""}`} />
          </Button>
          <Button
            variant="outline"
            onClick={() => testMutation.mutate("contact")}
            disabled={testMutation.isPending}
            className="gap-2"
          >
            <Send className={`h-4 w-4 ${testMutation.isPending ? "animate-pulse" : ""}`} />
            {testMutation.isPending ? "Sender…" : "Test kontaktbesked"}
          </Button>
          <Button
            onClick={() => testMutation.mutate("inquiry")}
            disabled={testMutation.isPending}
            className="gap-2"
          >
            <Send className={`h-4 w-4 ${testMutation.isPending ? "animate-pulse" : ""}`} />
            {testMutation.isPending ? "Sender…" : "Test prøvetur / bilforespørgsel"}
          </Button>
        </div>
      </div>

      <EmailVerificationAlertBanner
        alerts={alertsQuery.data?.alerts}
        targetRecipient={alertsQuery.data?.targetRecipient}
        thresholdHours={alertThreshold}
        onThresholdChange={setAlertThreshold}
        isLoading={alertsQuery.isLoading}
        templateLabels={TEMPLATE_LABELS}
        onResendComplete={() => {
          void alertsQuery.refetch();
          void query.refetch();
        }}
      />

      {lastTest && (
        <Card>
          <CardContent className="py-4">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              <div className="space-y-1">
                <div className="flex items-center gap-2 text-sm font-medium">
                  {lastTest.status === "pending" || lastTest.status === "sent" ? (
                    <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                  ) : lastTest.status === "failed" || lastTest.status === "dlq" ? (
                    <XCircle className="h-4 w-4 text-destructive" />
                  ) : (
                    <AlertTriangle className="h-4 w-4 text-amber-600" />
                  )}
                  Seneste testmail til{" "}
                  <span className="font-mono">{lastTest.target}</span>
                </div>
                <div className="text-xs text-muted-foreground font-mono break-all">
                  message-id: {lastTest.messageId ?? "—"}
                </div>
                <div className="text-xs text-muted-foreground">
                  Logget: {formatDate(lastTest.createdAt)} · skabelon{" "}
                  <span className="font-mono">{lastTest.template}</span>
                </div>
              </div>
              <div>{statusBadge(lastTest.status ?? "pending")}</div>
            </div>

            <div className="mt-4 border-t pt-3">
              <div className="flex items-center gap-2 text-sm font-medium mb-2">
                {lastTest.renderError ? (
                  <XCircle className="h-4 w-4 text-destructive" />
                ) : lastTest.allFieldsValid ? (
                  <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                ) : (
                  <AlertTriangle className="h-4 w-4 text-amber-600" />
                )}
                Indholds-validering ({lastTest.kind === "inquiry"
                  ? "prøvetur / bilforespørgsel"
                  : "kontaktbesked"})
              </div>
              {lastTest.renderError ? (
                <p className="text-xs text-destructive">
                  Skabelonen kunne ikke rendres: {lastTest.renderError}
                </p>
              ) : (
                <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 text-xs">
                  {lastTest.fieldChecks.map((c) => (
                    <li key={c.field} className="flex items-start gap-2">
                      {c.found ? (
                        <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600 mt-0.5 shrink-0" />
                      ) : (
                        <XCircle className="h-3.5 w-3.5 text-destructive mt-0.5 shrink-0" />
                      )}
                      <span>
                        <span className="font-medium">{c.label}:</span>{" "}
                        <span
                          className={
                            c.found
                              ? "text-muted-foreground"
                              : "text-destructive"
                          }
                        >
                          {c.found ? "fundet" : "mangler"} —{" "}
                          <span className="font-mono break-all">
                            {c.expected.length > 50
                              ? c.expected.slice(0, 50) + "…"
                              : c.expected}
                          </span>
                        </span>
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {query.isLoading && (
        <Card>
          <CardContent className="py-10 text-center text-muted-foreground">
            Henter email-historik…
          </CardContent>
        </Card>
      )}

      {query.isError && (
        <Card>
          <CardContent className="py-6 text-destructive">
            Kunne ikke hente data: {(query.error as Error)?.message}
          </CardContent>
        </Card>
      )}

      <TemplateRecipientsCard />

      <ResendFailedContactsCard />

      <ContactNotificationDispatchCard />

      <EmailQueueTimelineCard />

      {query.data && (
        <Card>
          <CardContent className="py-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                Filter
              </span>
              <Tabs
                value={statusFilter}
                onValueChange={(v) => setStatusFilter(v as StatusFilter)}
              >
                <TabsList>
                  {STATUS_FILTER_OPTIONS.map((o) => (
                    <TabsTrigger key={o.value} value={o.value}>
                      {o.label}
                    </TabsTrigger>
                  ))}
                </TabsList>
              </Tabs>
            </div>
            <div className="flex items-center gap-2">
              <Switch
                id="target-only"
                checked={targetOnly}
                onCheckedChange={setTargetOnly}
              />
              <Label htmlFor="target-only" className="text-sm cursor-pointer">
                Kun til{" "}
                <span className="font-mono">{query.data.targetRecipient}</span>
              </Label>
            </div>
          </CardContent>
        </Card>
      )}

      {query.data && (
        <div className="grid gap-6 lg:grid-cols-2">
          {query.data.summaries.map((s) => {
            const reached = s.targetReachedCount > 0;
            const hasFailures =
              s.lastFailureAt !== null &&
              (!s.lastSuccessAt || new Date(s.lastFailureAt) > new Date(s.lastSuccessAt));
            const filteredEvents = s.events.filter((e) =>
              eventMatchesFilters(e, {
                status: statusFilter,
                targetOnly,
                targetRecipient: query.data!.targetRecipient,
              }),
            );
            const filtersActive = statusFilter !== "all" || targetOnly;
            return (
              <Card key={s.templateName} className="overflow-hidden">
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <CardTitle className="text-base">
                        {TEMPLATE_LABELS[s.templateName] ?? s.templateName}
                      </CardTitle>
                      <p className="text-xs text-muted-foreground mt-1 font-mono">
                        {s.templateName}
                      </p>
                    </div>
                    {reached ? (
                      <Badge className="bg-emerald-500/15 text-emerald-700 border-emerald-500/30 gap-1">
                        <CheckCircle2 className="h-3.5 w-3.5" />
                        Nået {query.data!.targetRecipient}
                      </Badge>
                    ) : hasFailures ? (
                      <Badge variant="destructive" className="gap-1">
                        <XCircle className="h-3.5 w-3.5" />
                        Senest fejlet
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="gap-1">
                        <AlertTriangle className="h-3.5 w-3.5" />
                        Ingen leverance
                      </Badge>
                    )}
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
                    <Stat label="Total events" value={String(s.totalEvents)} />
                    <Stat
                      label={`Til ${query.data!.targetRecipient}`}
                      value={String(s.targetReachedCount)}
                      tone={reached ? "success" : "muted"}
                    />
                    <Stat label="Seneste OK" value={formatDate(s.lastSuccessAt)} />
                    <Stat
                      label="Seneste fejl"
                      value={formatDate(s.lastFailureAt)}
                      tone={s.lastFailureAt ? "danger" : "muted"}
                    />
                  </div>

                  {filtersActive && (
                    <div className="text-xs text-muted-foreground">
                      Viser {filteredEvents.length} af {s.events.length} events
                      efter filter.
                    </div>
                  )}

                  {filteredEvents.length === 0 ? (
                    <div className="rounded-md border border-dashed p-6 text-center text-sm text-muted-foreground">
                      {s.events.length === 0
                        ? "Ingen registrerede sendinger i den valgte periode."
                        : "Ingen events matcher de valgte filtre."}
                    </div>
                  ) : (
                    <div className="rounded-md border overflow-hidden">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead className="w-[150px]">Tidspunkt</TableHead>
                            <TableHead>Modtager / detaljer</TableHead>
                            <TableHead className="w-[110px]">Status</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {filteredEvents.map((e) => (
                            <EventRow
                              key={e.id}
                              event={e}
                              isTarget={
                                (e.recipient_email ?? "").toLowerCase() ===
                                query.data!.targetRecipient
                              }
                            />
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {query.data && <RecentFailuresCard summaries={query.data.summaries} />}
    </div>
  );
}

function isFailureStatus(status: string): boolean {
  return status === "failed" || status === "dlq" || status === "bounced";
}

function formatMetadata(metadata: VerificationEvent["metadata"]): string | null {
  if (metadata == null) return null;
  if (typeof metadata === "string") return metadata;
  try {
    const json = JSON.stringify(metadata, null, 2);
    if (!json || json === "{}" || json === "null") return null;
    return json;
  } catch {
    return String(metadata);
  }
}

function EventRow({
  event,
  isTarget,
}: {
  event: VerificationEvent;
  isTarget: boolean;
}) {
  const [open, setOpen] = useState(false);
  const failure = isFailureStatus(event.status);
  const metadataText = formatMetadata(event.metadata);
  const hasDetails = Boolean(event.error_message || metadataText || event.message_id);

  return (
    <TableRow className={failure ? "bg-destructive/5" : undefined}>
      <TableCell className="text-xs text-muted-foreground whitespace-nowrap align-top">
        {formatDate(event.created_at)}
      </TableCell>
      <TableCell className="text-xs align-top">
        <span
          className={
            isTarget
              ? "font-semibold text-foreground"
              : "text-muted-foreground"
          }
        >
          {event.recipient_email}
        </span>
        {event.error_message && (
          <div className="mt-1 text-[11px] text-destructive break-words">
            {event.error_message}
          </div>
        )}
        {hasDetails && (
          <Collapsible open={open} onOpenChange={setOpen}>
            <CollapsibleTrigger asChild>
              <button
                type="button"
                className="mt-1 inline-flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground underline-offset-2 hover:underline"
              >
                <ChevronDown
                  className={`h-3 w-3 transition-transform ${open ? "rotate-180" : ""}`}
                />
                {open ? "Skjul" : "Vis"} provider-detaljer
              </button>
            </CollapsibleTrigger>
            <CollapsibleContent className="mt-2 space-y-2">
              {event.message_id && (
                <div className="text-[11px] font-mono break-all text-muted-foreground">
                  message-id: {event.message_id}
                </div>
              )}
              {metadataText && (
                <pre className="rounded-md bg-muted/50 border p-2 text-[11px] font-mono whitespace-pre-wrap break-all max-h-64 overflow-auto">
                  {metadataText}
                </pre>
              )}
              {!metadataText && !event.error_message && (
                <div className="text-[11px] text-muted-foreground italic">
                  Ingen yderligere provider-respons logget for denne mail.
                </div>
              )}
            </CollapsibleContent>
          </Collapsible>
        )}
      </TableCell>
      <TableCell className="align-top">{statusBadge(event.status)}</TableCell>
    </TableRow>
  );
}

function RecentFailuresCard({
  summaries,
}: {
  summaries: { templateName: string; recentFailures: VerificationEvent[] }[];
}) {
  const failures = summaries
    .flatMap((s) =>
      s.recentFailures.map((f) => ({ ...f, templateName: s.templateName })),
    )
    .sort((a, b) => b.created_at.localeCompare(a.created_at))
    .slice(0, 15);

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <XCircle className="h-4 w-4 text-destructive" />
          Seneste fejl & provider-svar
        </CardTitle>
        <p className="text-xs text-muted-foreground">
          Detaljeret fejlbesked og payload fra email-providerens svar — brug
          dette til at fejlfinde hvorfor mails ikke kommer frem i produktion.
        </p>
      </CardHeader>
      <CardContent>
        {failures.length === 0 ? (
          <div className="rounded-md border border-dashed p-6 text-center text-sm text-muted-foreground">
            <CheckCircle2 className="mx-auto h-5 w-5 text-emerald-600 mb-2" />
            Ingen fejl registreret i den valgte periode.
          </div>
        ) : (
          <div className="space-y-3">
            {failures.map((f) => {
              const metadataText = formatMetadata(f.metadata);
              return (
                <div
                  key={f.id}
                  className="rounded-md border border-destructive/30 bg-destructive/5 p-3 text-xs"
                >
                  <div className="flex flex-wrap items-center gap-2 justify-between">
                    <div className="flex items-center gap-2">
                      {statusBadge(f.status)}
                      <span className="font-mono text-[11px] text-muted-foreground">
                        {TEMPLATE_LABELS[f.templateName] ?? f.templateName}
                      </span>
                    </div>
                    <span className="text-[11px] text-muted-foreground">
                      {formatDate(f.created_at)}
                    </span>
                  </div>
                  <div className="mt-2 text-foreground">
                    Til <span className="font-mono">{f.recipient_email}</span>
                  </div>
                  {f.error_message && (
                    <div className="mt-1 text-destructive break-words">
                      <span className="font-medium">Fejl:</span> {f.error_message}
                    </div>
                  )}
                  {f.message_id && (
                    <div className="mt-1 text-[11px] font-mono break-all text-muted-foreground">
                      message-id: {f.message_id}
                    </div>
                  )}
                  {metadataText && (
                    <details className="mt-2">
                      <summary className="cursor-pointer text-[11px] text-muted-foreground hover:text-foreground">
                        Provider-payload
                      </summary>
                      <pre className="mt-2 rounded-md bg-background border p-2 text-[11px] font-mono whitespace-pre-wrap break-all max-h-64 overflow-auto">
                        {metadataText}
                      </pre>
                    </details>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function Stat({
  label,
  value,
  tone = "default",
}: {
  label: string;
  value: string;
  tone?: "default" | "success" | "danger" | "muted";
}) {
  const toneClass =
    tone === "success"
      ? "text-emerald-700"
      : tone === "danger"
        ? "text-destructive"
        : tone === "muted"
          ? "text-muted-foreground"
          : "text-foreground";
  return (
    <div className="rounded-md border bg-muted/30 px-3 py-2">
      <div className="text-[11px] uppercase tracking-wide text-muted-foreground">
        {label}
      </div>
      <div className={`text-sm font-medium ${toneClass} truncate`}>{value}</div>
    </div>
  );
}
